/**
 * Parse a country/region list that may use mixed delimiters:
 *   - Multi-line: "Kenya\r\n- Uganda\r\n- Tanzania, United Republic"
 *   - Semicolons: "Australia; Ethiopia; India"
 * Handles both formats in one pass.
 */
export function parseCountryList(str) {
  if (!str) return [];
  return str.split(/\r?\n/)
    .flatMap(line => line.split(';'))
    .map(s => s.replace(/^[-\s]+/, '').trim())
    .filter(s => s && s !== 'Not applicable' && s !== 'Not Applicable');
}

// Parse initiative/program codes from strings like "INIT-05; INIT-06" or "SP04 - Multifunctional Landscapes"
// Normalizes full names to just the code (e.g., "SP04 - Multifunctional Landscapes" → "SP04")
export function parseInitiatives(initString) {
  if (!initString) return [];
  return initString.split(/[,;]/).map(s => {
    const trimmed = s.trim();
    // Extract just the program/initiative code (SP01, INIT-05, etc.)
    const codeMatch = trimmed.match(/^(SP\d+|INIT-\d+|PLAT-\d+|SGP-\d+)/);
    return codeMatch ? codeMatch[1] : trimmed;
  }).filter(Boolean);
}

/**
 * Parse a structured ToC Mapping string into individual entries.
 *
 * Input format (one or more entries concatenated):
 *   (SP08 - Food Frontiers and Security)  Title: 2.3.3 Recover - Description: Guidance...
 *   (SP10 - Gender Equality and Inclusion)  Title: 2.2.1: Research prioritization... - Description: ...
 *
 * Returns: [{ program: 'SP08', shortTitle: '2.3.3 Recover', description: '...' }, ...]
 *
 * Handles SP, INIT, SGP, PLAT codes.  Validated at 100% parse rate for SP programs (7247/7273 overall).
 */
export function parseTocMappingEntries(tocMappingStr) {
  if (!tocMappingStr) return [];

  const text = String(tocMappingStr);

  // Split on entry boundaries — each entry starts with (SPxx - ...) or (INIT-xx ...) etc.
  const entryPattern = /(?=\((?:SP\d{2}|INIT-\d+|SGP-\d+|PLAT-\d+)\s*-)/;
  const chunks = text.split(entryPattern).filter(s => s.trim());

  const results = [];
  // Pattern: (CODE - Name)  Title: SHORT_TITLE - Description: DESC
  const parseRe = /^\(([A-Z]+-?\d+)\s*-[^)]*\)\s*Title:\s*(.*?)\s*-\s*Description:\s*(.*)/s;

  for (const chunk of chunks) {
    const m = chunk.trim().match(parseRe);
    if (!m) continue;

    const program = m[1];
    // Normalize shortTitle: collapse newlines/whitespace, strip trailing colons
    const shortTitle = m[2].replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').replace(/:+\s*$/, '').trim();
    const description = m[3].replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

    results.push({ program, shortTitle, description });
  }

  return results;
}

// Classify a result into one of three TOC alignment pathways
// This is an internal helper used by calculateStats and parseResultsData
export function classifyTocAlignment(row) {
  const hasTocMapping = Boolean(
    row['TOC results'] || row['ToC Mapping'] ||
    row['ToC Mapping / ToC Details'] || row['ToC Details (down to indicator)']
  );
  const hasIndicator = Boolean(
    row['Indicator used'] || row['Indicator Used'] ||
    row['Primary Indicator used'] || row['Contributor Indicator used']
  );
  const isPlanned = (row['Is Planned?'] || '').toString().trim().toLowerCase() === 'yes';

  if (hasTocMapping && hasIndicator && isPlanned) return 'aligned-planned';
  if (hasTocMapping && hasIndicator) return 'aligned-unplanned';
  if (hasTocMapping) return 'linked';
  return 'other';
}
